<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatReportDuration } from '../constants';
import { buildBucketArchiveFilters } from '../helpers/summaryReportArchiveHelper';
import { useSummaryReportArchive } from '../composables/useSummaryReportArchive';
import { useReportTablePagination } from '../composables/useReportTablePagination';
import { resolveDynamicsGranularity } from '../helpers/dynamicsGranularityHelper';
import Pagination from 'dashboard/components/table/Pagination.vue';

const props = defineProps({
  rows: {
    type: Array,
    default: () => [],
  },
  archiveContext: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const { openArchive } = useSummaryReportArchive();

const granularity = computed(() =>
  resolveDynamicsGranularity(props.archiveContext.from, props.archiveContext.to)
);

const formatCount = value =>
  value || value === 0 ? Number(value).toLocaleString() : '--';

const openBucket = (row, metricKey, rawValue) => {
  if (!row?.date || !rawValue) return;
  const filters = buildBucketArchiveFilters({
    metricKey,
    reportType: 'account',
    row: null,
    context: props.archiveContext,
    dateLabel: row.date,
    granularity: granularity.value,
  });
  openArchive({
    metricKey,
    reportType: 'account',
    row: null,
    context: props.archiveContext,
    filters,
  });
};

const tableRows = computed(() =>
  (props.rows || []).map(row => ({
    date: row.date,
    created: row.created,
    reopened: row.reopened,
    botResolved: row.bot_resolved ?? row.botResolved,
    humanResolved: row.human_resolved ?? row.humanResolved,
    medianFrt: formatReportDuration(
      row.median_first_response ?? row.medianFirstResponse,
      t
    ),
    medianResolution: formatReportDuration(
      row.median_resolution ?? row.medianResolution,
      t
    ),
    withTransfers: row.with_transfers ?? row.withTransfers,
  }))
);

const { table, pagedRows, getPageSize, handlePageSizeChange } =
  useReportTablePagination(tableRows, 'chats_daily_detail_page_size');
</script>

<template>
  <section
    class="relative mt-5 px-2 py-2 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
  >
    <h3 class="px-2 py-2 text-base font-medium text-n-slate-12">
      {{ $t('CHATS_REPORTS.DAILY_DETAIL.TITLE') }}
    </h3>
    <div class="overflow-x-auto">
      <table class="w-full min-w-[720px] text-xs">
        <thead>
          <tr class="border-b border-n-weak text-[10px] text-n-slate-11">
            <th class="px-2 py-2 text-left font-medium">
              {{ $t('CHATS_REPORTS.DAILY_DETAIL.DATE') }}
            </th>
            <th class="px-2 py-2 text-center font-medium">
              {{ $t('CHATS_REPORTS.DAILY_DETAIL.CREATED') }}
            </th>
            <th class="px-2 py-2 text-center font-medium">
              {{ $t('CHATS_REPORTS.DAILY_DETAIL.REOPENED') }}
            </th>
            <th class="px-2 py-2 text-center font-medium">
              {{ $t('CHATS_REPORTS.DAILY_DETAIL.BOT_RESOLVED') }}
            </th>
            <th class="px-2 py-2 text-center font-medium">
              {{ $t('CHATS_REPORTS.DAILY_DETAIL.HUMAN_RESOLVED') }}
            </th>
            <th class="px-2 py-2 text-center font-medium">
              {{ $t('CHATS_REPORTS.DAILY_DETAIL.MEDIAN_FRT') }}
            </th>
            <th class="px-2 py-2 text-center font-medium">
              {{ $t('CHATS_REPORTS.DAILY_DETAIL.MEDIAN_RESOLUTION') }}
            </th>
            <th class="px-2 py-2 text-center font-medium">
              {{ $t('CHATS_REPORTS.DAILY_DETAIL.WITH_TRANSFERS') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in pagedRows"
            :key="row.date"
            class="border-b border-n-weak/60 last:border-b-0"
          >
            <td class="px-2 py-2 text-left text-n-slate-12">{{ row.date }}</td>
            <td class="px-2 py-2 text-center">
              <button
                v-if="row.created"
                type="button"
                class="tabular-nums text-n-brand hover:underline"
                @click="openBucket(row, 'conversationsCount', row.created)"
              >
                {{ formatCount(row.created) }}
              </button>
              <span v-else class="tabular-nums text-n-slate-11">--</span>
            </td>
            <td class="px-2 py-2 text-center">
              <button
                v-if="row.reopened"
                type="button"
                class="tabular-nums text-n-brand hover:underline"
                @click="openBucket(row, 'reopenedCount', row.reopened)"
              >
                {{ formatCount(row.reopened) }}
              </button>
              <span v-else class="tabular-nums text-n-slate-11">--</span>
            </td>
            <td class="px-2 py-2 text-center">
              <button
                v-if="row.botResolved"
                type="button"
                class="tabular-nums text-n-brand hover:underline"
                @click="openBucket(row, 'botResolvedCount', row.botResolved)"
              >
                {{ formatCount(row.botResolved) }}
              </button>
              <span v-else class="tabular-nums text-n-slate-11">--</span>
            </td>
            <td class="px-2 py-2 text-center">
              <button
                v-if="row.humanResolved"
                type="button"
                class="tabular-nums text-n-brand hover:underline"
                @click="
                  openBucket(row, 'humanResolvedCount', row.humanResolved)
                "
              >
                {{ formatCount(row.humanResolved) }}
              </button>
              <span v-else class="tabular-nums text-n-slate-11">--</span>
            </td>
            <td class="px-2 py-2 text-center">
              <button
                v-if="row.medianFrt && row.medianFrt !== '--'"
                type="button"
                class="tabular-nums text-n-brand hover:underline"
                @click="openBucket(row, 'firstResponseCount', 1)"
              >
                {{ row.medianFrt }}
              </button>
              <span v-else class="tabular-nums text-n-slate-11">--</span>
            </td>
            <td class="px-2 py-2 text-center">
              <button
                v-if="row.medianResolution && row.medianResolution !== '--'"
                type="button"
                class="tabular-nums text-n-brand hover:underline"
                @click="openBucket(row, 'resolutionsCount', 1)"
              >
                {{ row.medianResolution }}
              </button>
              <span v-else class="tabular-nums text-n-slate-11">--</span>
            </td>
            <td class="px-2 py-2 text-center">
              <button
                v-if="row.withTransfers"
                type="button"
                class="tabular-nums text-n-brand hover:underline"
                @click="openBucket(row, 'transfersOutCount', row.withTransfers)"
              >
                {{ formatCount(row.withTransfers) }}
              </button>
              <span v-else class="tabular-nums text-n-slate-11">--</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <Pagination
      v-if="tableRows.length"
      class="mt-2 px-2"
      :table="table"
      show-page-size-selector
      :default-page-size="getPageSize()"
      @page-size-change="handlePageSizeChange"
    />
    <p
      v-if="!tableRows.length"
      class="px-2 py-6 text-center text-sm text-n-slate-11"
    >
      {{ $t('SUMMARY_REPORTS.CHARTS.NO_DATA') }}
    </p>
  </section>
</template>
